import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Col, Input, InputNumber, Modal, Row, Select, Spin, Table } from "antd";
import { useFormik } from "formik";
import { cloneDeep, get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/functional_components/Button/PFButton";
import { setLoading } from "../../redux/ducks/Loading";
import ParkAuto from "../../services/api/ParkAuto";
import CarEngineType from "../../services/api/CarEngineType";
import { carCategory, paginationDefaults } from "../../utils/properties";
import { buildQueryString, checkRoles, exportFile, showError, showSuccess, trimData } from "../../utils/utilities";
import Filter from "../../components/functional_components/Filter/Filter";
import { setFilters } from "../../redux/ducks/Filters";
import { useNavigate } from "react-router-dom";
const { Option } = Select;

const ParkAutos = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { confirm } = Modal;
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
    carEngineTypes: [],
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, { sort: "plate" });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  useEffect(() => {
    getCarEngineTypes();
  }, [state.tableContent]);

  const formikParkAuto = useFormik({
    initialValues: {
      brand: get(state.modal.data, "brand", null),
      calcKm: get(state.modal.data, "calcKm", null),
      category: get(state.modal.data, "category", null),
      cc: get(state.modal.data, "cc", null),
      carEngineType: get(state.modal.data, "carEngineType.id", null),
      model: get(state.modal.data, "model", null),
      plate: get(state.modal.data, "plate", null),
      powerKw: get(state.modal.data, "powerKw", null),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.brand) {
        errors.brand = "Required!";
      }
      if (!values.calcKm) {
        errors.calcKm = "Required!";
      }
      if (!values.category) {
        errors.category = "Required!";
      }
      if (!values.cc) {
        errors.cc = "Required!";
      }
      if (!values.carEngineType) {
        errors.carEngineType = "Required!";
      }
      if (!values.model) {
        errors.model = "Required!";
      }
      if (!values.plate) {
        errors.plate = "Required!";
      }
      if (!values.powerKw) {
        errors.powerKw = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        ...values,
        carEngineType: state.carEngineTypes.find((c) => c.id === values.carEngineType),
        id: state.modal.data?.id || undefined,
      };
      const saveResponse = await ParkAuto.setParkAuto(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, { sort: "plate" });
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } = await ParkAuto.getParkAutos(queryString);
    if (!error && data) {
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikParkAuto.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await ParkAuto.deleteParkAuto(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, { sort: "plate" });
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const columns = [
    {
      title: t("parkAuto.table.plate"),
      dataIndex: "plate",
      key: "plate",
    },
    {
      title: t("parkAuto.table.brand"),
      dataIndex: "brand",
      key: "brand",
      render: (_, data) => data.brand,
    },
    {
      title: t("parkAuto.table.model"),
      dataIndex: "model",
      key: "model",
      render: (_, data) => data.model,
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => navigate(`/park-auto/${data.id}`)}>
              <EditOutlined className="icon-medium" />
            </div>
            {checkRoles(userInfo.roles, ["SYSTEM_IT"]) && (
              <div onClick={() => deleteCallback(data.id)}>
                <DeleteOutlined className="icon-medium" />
              </div>
            )}
          </div>
        );
      },
    },
  ];
  const getCarEngineTypes = async () => {
    let carEngineTypes = {};
    setState((prevState) => ({ ...prevState, loading: true }));
    const { data: dataEngine, error: errorEngine } = await CarEngineType.getCarEngineTypes("allPages=true");
    if (!errorEngine && dataEngine) {
      carEngineTypes = dataEngine;
    } else {
      showError(get(errorEngine, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false, carEngineTypes }));
  };
  const formikFilter = useFormik({
    initialValues: {
      model: "",
      brand: "",
      carEngineType: "",
      plate: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      let dataFilter = cloneDeep(values);
      dataFilter["page"] = 0;
      dataFilter["carEngineType.id"] = values.carEngineType;
      dataFilter["sort"] = "plate";
      delete dataFilter.carEngineType;
      const queryString = buildQueryString(filters.obj, dataFilter);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "model",
      name: "model",
      value: formikFilter.values.model,
      label: t("parkAuto.table.model"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "input",
      isRequired: false,
      id: "brand",
      name: "brand",
      value: formikFilter.values.brand,
      label: t("parkAuto.table.brand"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "select",
      isRequired: false,
      id: "carEngineType",
      name: "carEngineType",
      value: formikFilter.values.carEngineType,
      label: t("carFuelRefund.table.carEngineType"),
      onChange: (e) => formikFilter.setFieldValue("carEngineType", e),
      onBlur: formikFilter.handleBlur("carEngineType"),
      // onSearch: (e) => onSearchCarEngineType(e),
      dataSelect: state.carEngineTypes.map((u) => ({ value: u.id, label: u.name })),
    },
    {
      type: "input",
      isRequired: false,
      id: "plate",
      name: "plate",
      value: formikFilter.values.plate,
      label: t("parkAuto.table.plate"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString(
      { page: paginationDefaults.page - 1, size: paginationDefaults.size },
      { sort: "plate" }
    );
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const handleDownload = () => async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const file = await ParkAuto.exportParkAuto();
    if (!file.error) {
      showSuccess();
      const blob = new Blob([file]);
      exportFile(blob, `export-park-auto.csv`);
    } else showError(get(file, "error.messageCode", "ER000"));

    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          className={"mr-10"}
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={handleDownload()}
        />
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize, sort: "plate" });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.brand
            ? state.modal.data.brand
            : t("parkAuto.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikParkAuto.submitForm}
            isDisabled={!(formikParkAuto.dirty && formikParkAuto.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="id">
                {t("parkAuto.table.category")}*
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                className="w-100"
                id="category"
                name="category"
                onChange={(value) => formikParkAuto.setFieldValue("category", value)}
                placeholder={t("parkAuto.table.category")}
                onBlur={formikParkAuto.handleBlur("category")}
                value={formikParkAuto.values.category}
              >
                {carCategory.map((data, idx) => (
                  <Option key={idx} value={data}>
                    {t(`carCategory.${data}`)}
                  </Option>
                ))}
              </Select>
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="plate">
                {t("parkAuto.table.plate")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="plate"
                name="plate"
                placeholder={t("parkAuto.table.plate")}
                onChange={formikParkAuto.handleChange}
                onBlur={formikParkAuto.handleBlur}
                value={formikParkAuto.values.plate}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="brand">
                {t("parkAuto.table.brand")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="brand"
                name="brand"
                onChange={formikParkAuto.handleChange}
                onBlur={formikParkAuto.handleBlur}
                value={formikParkAuto.values.brand}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="model">
                {t("parkAuto.table.model")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="model"
                name="model"
                onChange={formikParkAuto.handleChange}
                onBlur={formikParkAuto.handleBlur}
                value={formikParkAuto.values.model}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="carEngineType">
                {t("parkAuto.table.carEngineType")}*
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                className="w-100"
                id="carEngineType"
                name="carEngineType"
                onChange={(value) => formikParkAuto.setFieldValue("carEngineType", value)}
                placeholder={t("parkAuto.table.carEngineType")}
                onBlur={formikParkAuto.handleBlur("carEngineType")}
                value={formikParkAuto.values.carEngineType}
              >
                {state.carEngineTypes.map((data, idx) => (
                  <Option key={idx} value={data.id}>
                    {data.name}
                  </Option>
                ))}
              </Select>
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="calcKm">
                {t("parkAuto.table.calcKm", {
                  um: formikParkAuto.values.carEngineType
                    ? state.carEngineTypes.find((c) => c.id === formikParkAuto.values.carEngineType)?.efficiencyDesc
                    : "-",
                })}
                *
              </label>
            </Row>
            <Row>
              <InputNumber
                disabled={!formikParkAuto.values.carEngineType}
                className={!formikParkAuto.values.carEngineType ? "w-100" : "ant-input"}
                controls={false}
                required
                id="calcKm"
                name="calcKm"
                onChange={(e) => formikParkAuto.setFieldValue("calcKm", e)}
                onBlur={formikParkAuto.handleBlur}
                value={formikParkAuto.values.calcKm}
                precision={2}
                decimalSeparator=","
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="cc">
                {formikParkAuto.values.carEngineType
                  ? state.carEngineTypes.find((c) => c.id === formikParkAuto.values.carEngineType)?.measurementDesc
                  : "-"}
                *
              </label>
            </Row>
            <Row>
              <InputNumber
                disabled={!formikParkAuto.values.carEngineType}
                className={!formikParkAuto.values.carEngineType ? "w-100" : "ant-input"}
                controls={false}
                required
                id="cc"
                name="cc"
                onChange={(e) => formikParkAuto.setFieldValue("cc", e)}
                onBlur={formikParkAuto.handleBlur}
                value={formikParkAuto.values.cc}
                precision={2}
                decimalSeparator=","
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="powerKw">
                {t("parkAuto.table.powerKw")}*
              </label>
            </Row>
            <Row>
              <InputNumber
                className="ant-input"
                controls={false}
                required
                id="powerKw"
                name="powerKw"
                onChange={(e) => formikParkAuto.setFieldValue("powerKw", e)}
                onBlur={formikParkAuto.handleBlur}
                value={formikParkAuto.values.powerKw}
                precision={2}
                decimalSeparator=","
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ParkAutos;
